import { useCallback, useEffect, useState } from "react";
import { useIsTabVisible } from "../../../hooks/useIsTabVisible";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "store/store";
import { RootState } from "store/reducers";
import { useInterval } from "../../../hooks/useInterval";
import { NotificationType } from "store/notification/types";
import { addNotification } from "store/notification/slice";
import { client } from "api/playerClient";
import { BroadcastResponse } from "@switcherstudio/player-api-client";

const DEFAULT_POLLING_INTERVAL =
    parseInt(import.meta.env.VITE_DEFAULT_POLLING_INTERVAL || "") || 15000;

const useHandleProcessingBroadcastPolling = (broadcastId: string) => {
    // check if player tab is the active tab
    const isTabVisible = useIsTabVisible();
    const [delay, setDelay] = useState<number | null>(DEFAULT_POLLING_INTERVAL);
    const dispatch = useDispatch<AppDispatch>();
    const [singleBroadcastResponse, setSingleBroadcastResponse] =
        useState<BroadcastResponse>();

    const fetchSingleBroadcastResponse = useCallback(async () => {
        const response = await client.videos(broadcastId);
        setSingleBroadcastResponse(response);
    }, [broadcastId]);

    const { hasSetDefault } = useSelector((s: RootState) => s.videoSession);

    // Handle processing video to ready
    useEffect(() => {
        if (
            !hasSetDefault ||
            !singleBroadcastResponse ||
            singleBroadcastResponse?.videos?.[0]?.details?.readyToStream
        )
            return;

        const videoReady =
            singleBroadcastResponse?.videos?.[0]?.details?.status === "ready";

        if (videoReady) {
            // Notify the user that the video is now available
            dispatch(
                addNotification({
                    type: NotificationType.Info,
                    message: "To watch the full stream, ",
                    clickText: "refresh this page.",
                    clickAction: () => {
                        window.location.reload();
                    },
                    class: "interactives"
                })
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, hasSetDefault, singleBroadcastResponse]);

    // Enable/disable polling
    useEffect(() => {
        if (isTabVisible) return setDelay(DEFAULT_POLLING_INTERVAL);
        setDelay(null);
    }, [isTabVisible]);

    // Execute polling to get processing videos
    useInterval(async () => {
        if (!singleBroadcastResponse) return;
        await fetchSingleBroadcastResponse();
    }, delay);
};

export default useHandleProcessingBroadcastPolling;
